import * as Yup from 'yup';

// Lifecycle is all-or-nothing: PersonaLifecycleInput has three NonNull Ints, so
// either every field is filled in or the whole object is omitted (it is nullable
// on Persona, and the existing dev "Email User" persona has lifecycle: NULL).
const lifecycleFieldNames = [
    'wakeCadenceHours',
    'expectedLifespanDays',
    'cancellationLikelihoodPercent',
] as const;

const isBlank = (value: unknown) => value === '' || value === null || typeof value === 'undefined';

const lifecyclePartiallyFilled = (lifecycle: Record<string, unknown> | undefined) => {
    if (!lifecycle) {
        return false;
    }
    const filled = lifecycleFieldNames.filter(name => !isBlank(lifecycle[name]));
    return filled.length > 0 && filled.length < lifecycleFieldNames.length;
};

// The lifecycle inputs are legitimately blank when unset, but Yup casts '' to
// NaN for .number(), which would fail typeError on an empty (valid) form. Treat
// a blank as undefined so only genuinely-filled fields are range-checked.
const blankToUndefined = (value: unknown, originalValue: unknown) =>
    isBlank(originalValue) ? undefined : value;

export const RecordSchema = Yup.object()
                               .shape({
                                          name: Yup.string()
                                                   .trim()
                                                   .required('Required'),
                                          description: Yup.string()
                                                          .nullable(),
                                          // What the executor actually runs (FNP-967). NOT required:
                                          // a persona may legitimately be authored before its test
                                          // definitions exist, and `[ID]` is nullable on the input.
                                          testDefinitionIds: Yup.array()
                                                                .of(Yup.string()
                                                                       .trim()
                                                                       .required('A test definition id cannot be blank')),
                                          // ⚠️ DEPRECATED (FNP-968) — the executor no longer reads this.
                                          // Retained, and deliberately still NOT required, so existing
                                          // personas keep validating through the transition.
                                          actionCodes: Yup.array()
                                                          .of(Yup.object()
                                                                 .shape({
                                                                            // Free text on purpose — see WeightedActionCodesField.
                                                                            actionCode: Yup.string()
                                                                                           .trim()
                                                                                           .required('Action code is required'),
                                                                            weight: Yup.number()
                                                                                       .typeError('Weight must be a number')
                                                                                       .integer('Weight must be an integer')
                                                                                       .min(1, 'Weight must be at least 1')
                                                                                       .required('Weight is required'),
                                                                        })),
                                          lifecycle: Yup.object()
                                                        .shape({
                                                                   wakeCadenceHours: Yup.number()
                                                                                        .transform(blankToUndefined)
                                                                                        .typeError('Must be a number')
                                                                                        .integer('Must be an integer')
                                                                                        .min(1, 'Must be at least 1'),
                                                                   expectedLifespanDays: Yup.number()
                                                                                            .transform(blankToUndefined)
                                                                                            .typeError('Must be a number')
                                                                                            .integer('Must be an integer')
                                                                                            .min(1, 'Must be at least 1'),
                                                                   cancellationLikelihoodPercent: Yup.number()
                                                                                                     .transform(blankToUndefined)
                                                                                                     .typeError('Must be a number')
                                                                                                     .integer('Must be an integer')
                                                                                                     .min(0, 'Must be between 0 and 100')
                                                                                                     .max(100, 'Must be between 0 and 100'),
                                                               })
                                                        .test(
                                                            'lifecycle-all-or-nothing',
                                                            'Fill in all three lifecycle fields, or leave all three empty',
                                                            (value) => !lifecyclePartiallyFilled(value as Record<string, unknown> | undefined),
                                                        ),
                                      });
